import { API_TYPES } from "@src/appV2/api";
import { type WorkplaceUser } from "@src/appV2/FacilityUser/hooks/useFetchPaginatedWorkplaceUsers";
import { z } from "zod";

export const PLACEMENT_FORM_ERROR_CODES = {
  NO_PRIMARY_HIRING_MANAGER: "NO_PRIMARY_HIRING_MANAGER",
  UPCOMING_INTERVIEW_SMS_DISABLED: "UPCOMING_INTERVIEW_SMS_DISABLED",
  PRIMARY_HIRING_MANAGER_MISSING_JOB_TITLE: "PRIMARY_HIRING_MANAGER_MISSING_JOB_TITLE",
  INTERVIEW_WORKER_ETA_SMS_DISABLED: "INTERVIEW_WORKER_ETA_SMS_DISABLED",
  PRIMARY_HIRING_MANAGER_MISSING_PHONE: "PRIMARY_HIRING_MANAGER_MISSING_PHONE",
} as const;

export type PlacementFormErrorCode =
  (typeof PLACEMENT_FORM_ERROR_CODES)[keyof typeof PLACEMENT_FORM_ERROR_CODES];

import { isDefined } from "@clipboard-health/util-ts";
import { isEmpty } from "lodash";
import { type FieldError, type FieldErrors } from "react-hook-form";

import {
  jobShiftPayRateFormSchema,
  jobShiftPayRateSchema,
  placementPayRateFormSchema,
  placementPayRateSchema,
} from "./placementPayRate.schema";

export const TIME_SLOTS = [
  { value: "am", label: "Day" },
  { value: "pm", label: "Evening" },
  { value: "noc", label: "Overnight" },
] as const;
export type TimeSlot = (typeof TIME_SLOTS)[number]["value"];

export const ROTATION_TYPES = ["am", "pm", "noc", "on", "off"] as const;
export type RotationType = (typeof ROTATION_TYPES)[number];

export const SCHEDULE_TYPES = [
  { value: "4_on_2_off", label: "4 days on, 2 days off" },
  { value: "flexible", label: "Flexible (e.g. 3 days/wk, weekdays only)" },
  { value: "fixed", label: "Fixed days of the week (e.g. Thu - Sat)" },
  { value: "other", label: "Other (e.g. alternating week rotation)" },
] as const;
export type ScheduleType = (typeof SCHEDULE_TYPES)[number]["value"];

export const WORKDAYS_REQUIRED = ["weekdays", "weekends", "both"] as const;
export type WorkdaysRequired = (typeof WORKDAYS_REQUIRED)[number];

export const PLACEMENT_STATUS = [
  {
    value: "draft",
    label: "Draft",
  },
  {
    value: "open",
    label: "Open",
  },
  {
    value: "waitlist",
    label: "Waitlist",
  },
  {
    value: "closed",
    label: "Closed",
  },
  {
    value: "archived",
    label: "Archived",
  },
] as const;

// eslint-disable-next-line no-restricted-syntax
export enum PlacementStatus {
  DRAFT = "draft",
  OPEN = "open",
  WAITLIST = "waitlist",
  CLOSED = "closed",
  ARCHIVED = "archived",
}

export const PLACEMENT_STATUS_DEFAULT_FILTER = [PlacementStatus.OPEN, PlacementStatus.DRAFT];

export const PLACEMENT_PERKS = [
  {
    value: "shiftDifferential",
    label: "Shift Differential",
    icon: "calendar-clock",
  },
  {
    value: "dailyPay",
    label: "Daily Pay",
    icon: "time-flies",
  },
  {
    value: "twelveShiftsAvailable",
    label: "12-Hour Shifts Available",
    icon: "date-clock",
  },
  {
    value: "doublesAvailable",
    label: "Double Shifts Available",
    icon: "clock-history",
  },
  {
    value: "signOnBonus",
    label: "Sign On Bonus",
    icon: "bolt-filled",
  },
  {
    value: "overtimeAvailable",
    label: "Overtime Available",
    icon: "clock",
  },
  {
    value: "newGradFriendly",
    label: "New Grad Friendly",
    icon: "school",
  },
] as const;

export type PlacementPerk = (typeof PLACEMENT_PERKS)[number]["value"];

export const PLACEMENT_TIER = {
  free: "free",
  premium: "premium",
} as const;
export const PLACEMENT_TIERS = Object.values(PLACEMENT_TIER);
export type PlacementTier = (typeof PLACEMENT_TIERS)[number];

const shiftRotationSchema = z.object({
  type: z.enum(ROTATION_TYPES),
  duration: z.number().min(1),
});

export const hiringManagerSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  email: z.string().optional(),
  roles: z.array(z.string()).optional(),
  jobTitle: z.string().optional(),
  phone: z.string().optional(),
  isPrimary: z.boolean().optional(),
});

export type HiringManager = z.infer<typeof hiringManagerSchema>;

export const jobShiftSchema = z.object({
  jobType: z.string(),
  shiftTypes: z.array(z.string()),
  shiftTypesWithTime: z
    .array(
      z.object({
        type: z.string(),
        startTime: z.string().optional(),
        endTime: z.string().optional(),
      })
    )
    .optional(),
  daysOfWeek: z.array(z.string()),
  rotation: z.array(shiftRotationSchema).default([]),
  minimumRequiredDaysPerWeek: z.number().optional(),
  notes: z.string().min(5).optional(),
  enabled: z.boolean().default(true),
  newGradFriendly: z.boolean().optional(),
  transparencyPayRates: jobShiftPayRateSchema.optional(),
});

export const createPlacementBodySchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.placement),
    attributes: z
      .object({
        workplaceId: z.string(),
        title: z.string(),
        description: z.string().optional(),
        tier: z.nativeEnum(PLACEMENT_TIER).optional(),
        hiringPOC: z
          .object({
            email: z.string().optional(),
            name: z.string().optional(),
          })
          .nullish(),
        jobShifts: z.array(jobShiftSchema),
        perks: z.array(z.string()),
        workerTypes: z.array(z.string()),
        validUntil: z.coerce.date().optional(),
        originalJobLink: z.string().optional(),
        webApplicationLink: z.string().optional(),
        payRange: z
          .object({
            minPay: z
              .object({
                amountInMinorUnits: z.coerce.number(),
                currencyCode: z.string(),
              })
              .optional(),
            maxPay: z
              .object({
                amountInMinorUnits: z.coerce.number(),
                currencyCode: z.string(),
              })
              .optional(),
          })
          .optional(),
        signOnBonus: z
          .object({
            amountInMinorUnits: z.coerce.number(),
            currencyCode: z.string(),
          })
          .optional(),
        schedule: z.string().optional(),
        exemptFromDocumentRequirements: z.boolean().optional(),
        transparencyPayRates: placementPayRateSchema.optional(),
        tagIds: z.array(z.string()).optional(),
        interviewInstructions: z.string().optional(),
        confirmedAt: z.string().optional(),
        status: z.nativeEnum(PlacementStatus).optional().default(PlacementStatus.OPEN),
      })
      .strict(),
    relationships: z
      .object({
        hiringManagers: z
          .object({
            data: z.array(
              z.object({
                type: z.literal(API_TYPES.workplaceUser),
                id: z.string(),
                meta: z
                  .object({
                    isPrimary: z.boolean().optional(),
                  })
                  .optional(),
              })
            ),
          })
          .optional(),
      })
      .optional(),
  }),
});

export type CreatePlacementRequest = z.infer<typeof createPlacementBodySchema>;

export interface PlacementFormSchemaContext {
  isChildcareType: boolean;
  isProspectWorkplace: boolean;
  workplaceUsers: WorkplaceUser[];
}

export function basePlacementFormSchema(context: PlacementFormSchemaContext) {
  const webApplicationLinkSchema = context.isProspectWorkplace
    ? z
        .string({ message: "Required for prospect workplaces" })
        .min(1, { message: "Required for prospect workplaces" })
    : z.string().optional();
  return z.object({
    workplaceId: z.string(),
    title: z
      .string({
        required_error: "Enter a job title",
      })
      .min(1, { message: "Enter a job title" }),
    tier: z.nativeEnum(PLACEMENT_TIER).optional(),
    description: z.string().optional(),
    workerTypes: z
      .array(z.string())
      .min(1, { message: "Select at least one qualification for the job" }),
    tagIds: context.isChildcareType
      ? z
          .array(z.string())
          .min(1, { message: "At least one age group is required for Child Care Centers" })
      : z.array(z.string()).optional(),
    hiringManagers: z.array(hiringManagerSchema),
    jobShifts: z
      .array(
        z
          .object({
            jobType: z
              .string({ required_error: "Select at least one job type" })
              .nonempty({ message: "Select at least one job type" }),
            shiftTypes: z.array(z.string()).min(1, "Select at least one shift time"),
            shiftTypesWithTime: z
              .array(
                z.object({
                  type: z.string(),
                  startTime: z.string().optional(),
                  endTime: z.string().optional(),
                })
              )
              .optional(),
            scheduleType: z.enum(["4_on_2_off", "flexible", "fixed", "other"]).optional(),
            daysOfWeek: z.array(z.string()),
            rotation: z
              .array(
                z.object({
                  type: z.enum(ROTATION_TYPES),
                  duration: z.number().min(1),
                })
              )
              .default([]),
            minimumRequiredDaysPerWeek: z.coerce.number().min(0).default(0),
            workdaysRequired: z.enum(WORKDAYS_REQUIRED).optional(),
            notes: z.string().optional(),
            enabled: z.boolean().default(true),
            newGradFriendly: z.boolean().optional(),
            transparencyPayRates: jobShiftPayRateFormSchema.optional(),
          })
          .refine(
            (shift) => {
              if (shift.scheduleType === "flexible") {
                return shift.workdaysRequired !== undefined;
              }

              return true;
            },
            {
              message: "Please specify the workdays required",
              path: ["workdaysRequired"],
            }
          )
          .refine(
            (shift) => {
              if (shift.scheduleType === "fixed") {
                return shift.daysOfWeek.length > 0;
              }

              return true;
            },
            {
              message: "Select at least one day for fixed schedule",
              path: ["daysOfWeek"],
            }
          )
          .refine(
            (shift) => {
              if (shift.scheduleType === "other") {
                return shift.notes !== undefined && shift.notes.trim().length > 0;
              }

              return true;
            },
            {
              message: "Please provide a description for the schedule",
              path: ["notes"],
            }
          )
      )
      .min(1, { message: "Select at least one job schedule" })
      .refine((jobShifts) => jobShifts.some((shift) => shift.enabled), {
        message: "Make sure at least one job schedule is enabled",
      }),
    perks: z.array(z.string()),
    validUntil: z.date().optional(),
    originalJobLink: z
      .string()
      .optional()
      .refine((value) => !value || z.string().url().safeParse(value).success, {
        message: "Enter a valid http(s) URL like https://example.com/job-posting",
      }),
    webApplicationLink: webApplicationLinkSchema.refine(
      (value) => !value || z.string().url().safeParse(value).success,
      {
        message: "Enter a valid http(s) URL like https://example.com/job-posting",
      }
    ),
    payRange: z
      .object({
        minPay: z.coerce
          .number()
          .min(0, { message: "Minimum pay must be greater than 0" })
          .optional(),
        maxPay: z.coerce
          .number()
          .min(0, { message: "Minimum pay must be greater than 0" })
          .optional(),
      })
      .refine((data) => !data.minPay || !data.maxPay || data.minPay <= data.maxPay, {
        message: "Maximum pay should be greater than minimum pay",
        path: ["maxPay"],
      })
      .optional(),
    signOnBonus: z
      .object({
        amount: z.coerce.number().min(0, { message: "Amount must be greater than 0" }).optional(),
      })
      .optional(),
    schedule: z
      .string()
      .max(1000, { message: "Schedule notes must be less than 1000 characters" })
      .optional(),
    exemptFromDocumentRequirements: z.boolean().optional(),
    overrideWorkplacePayRates: z.boolean().default(false),
    transparencyPayRates: placementPayRateFormSchema.optional(),
    interviewInstructions: z.string().optional(),
  });
}

export function withHiringManagersValidation({
  workplaceUsers,
  context,
  hiringManagers,
  isVerifiedJob,
}: {
  workplaceUsers: WorkplaceUser[];
  context: z.RefinementCtx;
  hiringManagers: Array<{ email?: string; id?: string; isPrimary?: boolean; jobTitle?: string }>;
  isVerifiedJob: boolean;
}) {
  if (!isVerifiedJob) {
    return;
  }

  hiringManagers.forEach((manager, index) => {
    if (!manager.id) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${
          isDefined(manager.email) ? manager.email : "This user"
        } doesn't belong to your workplace. Please create or remove this user.`,
        path: ["hiringManagers", index],
      });
      return;
    }

    if (workplaceUsers.length > 0) {
      const workplaceUser = workplaceUsers.find((user) => user.id === manager.id);

      if (!workplaceUser) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${manager.email ?? "This user"} doesn't exist in your workplace.`,
          path: ["hiringManagers", index], // This targets the specific item in the array
        });
      }
    }
  });

  const primaryHiringManager = hiringManagers.find((hiringManager) => hiringManager.isPrimary);
  const primaryWorkplaceUser = workplaceUsers.find((user) => user.id === primaryHiringManager?.id);

  if (!primaryWorkplaceUser) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Must have exactly one primary hiring manager.`,
      path: ["hiringManagers"],
      params: {
        errorCode: PLACEMENT_FORM_ERROR_CODES.NO_PRIMARY_HIRING_MANAGER,
      },
    });
    return;
  }

  // Collect all validation errors for the primary hiring manager
  const errorCodes: PlacementFormErrorCode[] = [];

  // Check job title requirement
  if (!primaryWorkplaceUser.designation || primaryWorkplaceUser.designation.trim().length === 0) {
    errorCodes.push(PLACEMENT_FORM_ERROR_CODES.PRIMARY_HIRING_MANAGER_MISSING_JOB_TITLE);
  }

  // Check Interview Worker ETA SMS notification requirement
  if (!primaryWorkplaceUser.alertSettings?.SMS.interviewWorkerETASMS?.enabled) {
    errorCodes.push(PLACEMENT_FORM_ERROR_CODES.INTERVIEW_WORKER_ETA_SMS_DISABLED);
  }

  // Check upcoming interview updates SMS notification requirement
  if (!primaryWorkplaceUser.alertSettings?.SMS.upcomingInterviewUpdatesSMS?.enabled) {
    errorCodes.push(PLACEMENT_FORM_ERROR_CODES.UPCOMING_INTERVIEW_SMS_DISABLED);
  }

  // Check user has phone number
  if (isEmpty(primaryWorkplaceUser.phone)) {
    errorCodes.push(PLACEMENT_FORM_ERROR_CODES.PRIMARY_HIRING_MANAGER_MISSING_PHONE);
  }

  // Add a single issue with all error codes if any validation failed
  if (errorCodes.length > 0) {
    const errorMessages = errorCodes.map((errorCode) => {
      switch (errorCode) {
        case PLACEMENT_FORM_ERROR_CODES.PRIMARY_HIRING_MANAGER_MISSING_JOB_TITLE: {
          return "Primary Hiring Manager must have a Job Title.";
        }
        case PLACEMENT_FORM_ERROR_CODES.INTERVIEW_WORKER_ETA_SMS_DISABLED: {
          return `Primary Hiring Manager (${primaryWorkplaceUser.name}) must have "Interview Worker ETA" notification enabled for SMS.`;
        }
        case PLACEMENT_FORM_ERROR_CODES.UPCOMING_INTERVIEW_SMS_DISABLED: {
          return `${primaryWorkplaceUser.name} must have "Send upcoming interview updates" SMS enabled.`;
        }
        case PLACEMENT_FORM_ERROR_CODES.PRIMARY_HIRING_MANAGER_MISSING_PHONE: {
          return `${primaryWorkplaceUser.name} must have a valid phone number.`;
        }
        case PLACEMENT_FORM_ERROR_CODES.NO_PRIMARY_HIRING_MANAGER: {
          return "";
        }
        default: {
          return "";
        }
      }
    });

    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: errorMessages.filter((message) => message.length > 0).join(" "),
      path: ["hiringManagers"],
      params: {
        errorCodes,
        workplaceUserId: primaryWorkplaceUser.id,
      },
    });
  }
}

// Reusable validation for jobShifts based on webApplicationLink
export function withJobShiftsValidation<T extends ReturnType<typeof basePlacementFormSchema>>(
  schema: T
): z.ZodEffects<T> {
  return schema.superRefine((data, context) => {
    // If webApplicationLink exists and is non-empty, jobShifts can be empty
    if (data.webApplicationLink && data.webApplicationLink.trim().length > 0) {
      return;
    }

    // Otherwise, check each job shift and add issue for each one missing scheduleType
    data.jobShifts.forEach((jobShift, index: number) => {
      if (jobShift.scheduleType === undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please select a schedule type",
          path: ["jobShifts", index, "scheduleType"],
        });
      }
    });
  });
}

export function placementFormSchema(formContext: PlacementFormSchemaContext) {
  return withJobShiftsValidation(basePlacementFormSchema(formContext)).superRefine(
    (data, context) => {
      const { isProspectWorkplace, workplaceUsers } = formContext;

      if (!isProspectWorkplace) {
        if (data.hiringManagers.length === 0) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Select at least one hiring manager",
            path: ["hiringManagers"],
          });
          return;
        }

        const isVerifiedJob =
          !data.webApplicationLink || data.webApplicationLink.trim().length === 0;
        withHiringManagersValidation({
          workplaceUsers,
          context,
          hiringManagers: data.hiringManagers,
          isVerifiedJob,
        });
      }
    }
  );
}

export type PlacementFormInput = z.infer<ReturnType<typeof placementFormSchema>>;

export type CreatePlacementFormInput = z.input<ReturnType<typeof placementFormSchema>>;
export type CreatePlacementFormOutput = z.infer<ReturnType<typeof placementFormSchema>>;

export function getHiringManagerErrorCodes(
  formHiringManagerErrors: FieldErrors<CreatePlacementFormInput>["hiringManagers"]
): Record<string, PlacementFormErrorCode[]> {
  let rawHiringManagerErrors: FieldError[] = [];
  if (Array.isArray(formHiringManagerErrors)) {
    rawHiringManagerErrors = formHiringManagerErrors as FieldError[];
  } else if (formHiringManagerErrors) {
    rawHiringManagerErrors = [formHiringManagerErrors as FieldError];
  }

  const hiringManagerErrors = rawHiringManagerErrors.filter(
    (
      error
    ): error is FieldError & {
      params: {
        workplaceUserId: string;
        errorCodes: PlacementFormErrorCode[];
      };
    } => {
      const errorCasted = error as FieldError & {
        params?: {
          workplaceUserId?: string;
          errorCodes?: string[];
        };
      };
      if (
        Array.isArray(errorCasted.params?.errorCodes) &&
        errorCasted.params.errorCodes.length > 0 &&
        errorCasted.params?.workplaceUserId
      ) {
        return errorCasted.params.errorCodes.every((code) =>
          Object.values(PLACEMENT_FORM_ERROR_CODES).includes(code as PlacementFormErrorCode)
        );
      }

      return false;
    }
  );

  return hiringManagerErrors.reduce<Record<string, PlacementFormErrorCode[]>>(
    (accumulator, error) => {
      const { workplaceUserId, errorCodes } = error.params;
      accumulator[workplaceUserId ?? ""] = [
        ...(accumulator[workplaceUserId ?? ""] || []),
        ...errorCodes,
      ];
      return accumulator;
    },
    {}
  );
}
